import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import Modal from '../../../../../ui/canon/Modal.jsx';
import Button from '../../../../../ui/canon/Button.jsx';
import ValuationChatWidget from './ValuationChatWidget';
import MarkdownMessage from '../../../assistant/components/MarkdownMessage.jsx';
import CollapsibleSources from '../../../assistant/components/CollapsibleSources.jsx';
import { fixMarkdownLineBreaks } from '../../../assistant/utils/markdownUtils';
import { formatAIChatTime } from '../Messaging/shared/dateUtils';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { Loader2 as Loader2Icon, Send as SendIcon, BookOpen as BookOpenIcon } from 'lucide-react';
import CopyMessageButton from './CopyMessageButton';

// Wrapper component for assistant message content that provides a ref for copying
const AssistantMessageContent = ({ message }) => {
  const contentRef = React.useRef(null);

  return (
    <>
      <div ref={contentRef}>
        <div
          className="chat_result text-sm break-words overflow-hidden text-succession-text-primary"
          dangerouslySetInnerHTML={{ __html: message.content }}
        />
        {/* Widget */}
        {message.widget && (
          <div className="mt-3 overflow-hidden">
            <ValuationChatWidget widget={message.widget} />
          </div>
        )}
      </div>
      <CopyMessageButton
        containerRef={contentRef}
        isHtml={true}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100"
      />
    </>
  );
};

// GraphQL mutation for agent interactions
const INTERACT_WITH_AGENT = gql`
  mutation InteractWithAgent($agentType: String!, $message: String!, $conversationId: String, $context: JSON!) {
    interactWithAgent(agentType: $agentType, message: $message, conversationId: $conversationId, context: $context) {
      response
      widgetType
      widgetProps
      nextActions
      updatedDcfParams
      ragSources
      chatHistory {
        id
        role
        content
        timestamp
        suggestedActions
        processingTime
      }
      conversationInfo {
        sessionId
        startedAt
        messageCount
        lastActivity
        topicsDiscussed
        durationMinutes
      }
    }
  }
`;

/**
 * ValuationChatWindow - Valuation agent chat using AssistantModalV2
 *
 * Wraps AssistantModalV2 with valuation-specific functionality:
 * - Agent interaction via GraphQL
 * - Valuation widgets
 * - DCF report links
 * - Chat history persistence
 */
const ValuationChatWindow = ({
  user,
  currentUser,
  onClose,
  initialConfidence,
  initialRecommendation,
  pageContext = 'other',
  dcfId = null,
  clientState = null,
  onDcfUpdate = null,
  conversationId: persistedConversationId = null, // Persisted from parent
  onConversationIdChange = null // Callback to update parent
}) => {
  const intl = useIntl();
  const [interactWithAgent] = useMutation(INTERACT_WITH_AGENT);
  const [messages, setMessages] = useState([]);
  const [agentId, setAgentId] = useState(persistedConversationId); // Initialize from persisted value
  const [chatData, setChatData] = useState({});
  const [conversationInfo, setConversationInfo] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [currentDcfId, setCurrentDcfId] = useState(null);
  const [isThinking, setIsThinking] = useState(false);
  const [modalMessages, setModalMessages] = useState([]);
  
  // Sync agentId back to parent when it changes
  useEffect(() => {
    if (agentId && onConversationIdChange) {
      onConversationIdChange(agentId);
    }
  }, [agentId, onConversationIdChange]);

  // Initialize agent and load chat history
  useEffect(() => {
    const initializeValuationAgent = async () => {
      setIsLoadingHistory(true);

      console.log('=== INITIALIZING VALUATION AGENT ===');
      console.log('Page Context:', pageContext);
      console.log('DCF ID:', dcfId);
      console.log('Client State:', clientState);
      console.log('Client State keys:', clientState ? Object.keys(clientState) : 'null');
      console.log('Client State value_of_practice:', clientState?.value_of_practice);
      console.log('====================================');

      try {
        const { data: response } = await interactWithAgent({
          variables: {
            agentType: 'valuation_chat_agent',
            message: 'INIT_WELCOME_MESSAGE',
            context: {
              chat_data: chatData,
              user_id: user.id,
              session_id: null,
              page_context: pageContext,
              dcf_id: dcfId,
              client_state: clientState
            }
          }
        });

        if (response && response.interactWithAgent) {
          const { chatHistory, conversationInfo, response: welcomeResponse, widgetType, widgetProps } = response.interactWithAgent;

          if (conversationInfo) {
            setConversationInfo(conversationInfo);
            setAgentId(conversationInfo.sessionId);
          }

          // Load existing chat history or start with welcome message
          console.log('Chat history from backend:', chatHistory);
          console.log('Welcome response from backend:', welcomeResponse);
          console.log('Widget type:', widgetType, 'Widget props:', widgetProps);
          console.log('Client State provided:', !!clientState);

          // IMPORTANT: If clientState is provided (valuation page), ALWAYS use fresh welcome
          // This ensures the agent shows current unsaved values, not old conversation data
          const shouldUseFreshWelcome = clientState && clientState.fee_based_revenue !== undefined;

          if (shouldUseFreshWelcome && welcomeResponse) {
            // On valuation page with current state - use fresh welcome
            console.log('Using FRESH welcome message (valuation page with client state)');
            const welcomeMsg = createWelcomeMessage(welcomeResponse, widgetType, widgetProps);
            setMessages([welcomeMsg]);
            setModalMessages([welcomeMsg]);
          } else if (chatHistory && chatHistory.length > 0) {
            // Load existing conversation history
            const convertedMessages = chatHistory
              .filter(msg => msg.role !== 'system')
              .filter(msg => msg.content !== 'INIT_WELCOME_MESSAGE' && msg.content !== 'START_NEW_CONVERSATION') // Filter out system init messages
              .map(msg => convertToModalFormat(msg));

            console.log('Converted messages from history:', convertedMessages);

            if (convertedMessages.length > 0) {
              setMessages(convertedMessages);
              setModalMessages(convertedMessages);
            } else {
              // No valid history, use welcome response
              console.log('No valid history, creating welcome from response');
              const welcomeMsg = createWelcomeMessage(welcomeResponse, widgetType, widgetProps);
              console.log('Created welcome message:', welcomeMsg);
              setMessages([welcomeMsg]);
              setModalMessages([welcomeMsg]);
            }
          } else if (welcomeResponse) {
            // No history, create welcome message from response
            console.log('No history, creating welcome from response');
            const welcomeMsg = createWelcomeMessage(welcomeResponse, widgetType, widgetProps);
            console.log('Created welcome message:', welcomeMsg);
            setMessages([welcomeMsg]);
            setModalMessages([welcomeMsg]);
          } else {
            // Fallback if nothing works
            console.log('Using fallback welcome');
            const fallbackWelcome = createFallbackWelcome();
            setMessages([fallbackWelcome]);
            setModalMessages([fallbackWelcome]);
          }
        } else {
          const fallbackWelcome = createFallbackWelcome();
          setMessages([fallbackWelcome]);
          setModalMessages([fallbackWelcome]);
        }
      } catch (error) {
        console.error('Failed to initialize valuation agent:', error);
        const fallbackWelcome = createFallbackWelcome();
        setMessages([fallbackWelcome]);
        setModalMessages([fallbackWelcome]);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    initializeValuationAgent();
  }, [initialConfidence, initialRecommendation]);

  const createFallbackWelcome = () => ({
    id: `msg-${Date.now()}`,
    role: 'assistant',
    content: intl.formatMessage({ id: 'succession.agent_system.valuation_chat_window.fallback_welcome' }),
    timestamp: formatAIChatTime(new Date()),
    suggestedActions: []
  });

  const createWelcomeMessage = (response, widgetType = null, widgetProps = null) => {
    const parsedResponse = parseAgentResponse(response);

    if (parsedResponse.dcfId) {
      setCurrentDcfId(parsedResponse.dcfId);
    }

    // Use widget from parsed response, or from GraphQL fields if not found
    let finalWidget = parsedResponse.widget;
    if (!finalWidget && widgetType && widgetProps) {
      finalWidget = {
        type: widgetType,
        data: widgetProps
      };
    }

    return {
      id: `msg-${Date.now()}`,
      role: 'assistant',
      content: parsedResponse.message,
      timestamp: formatAIChatTime(new Date()),
      widget: finalWidget,
      suggestedActions: parsedResponse.suggestedActions || [],
      dcfId: parsedResponse.dcfId || null
    };
  };

  const convertToModalFormat = (msg) => {
    // For assistant messages, parse the content to extract widgets, DCF IDs, etc.
    if (msg.role === 'assistant' || msg.role === 'agent') {
      const parsedResponse = parseAgentResponse(msg.content);

      if (parsedResponse.dcfId) {
        setCurrentDcfId(parsedResponse.dcfId);
      }

      return {
        id: msg.id || `msg-${Date.now()}`,
        role: 'assistant',
        content: parsedResponse.message,
        timestamp: formatAIChatTime(new Date(msg.timestamp)),
        suggestedActions: parsedResponse.suggestedActions || msg.suggestedActions || [],
        processingTime: msg.processingTime,
        dcfId: parsedResponse.dcfId || msg.dcfId || null,
        widget: parsedResponse.widget || msg.widget || null
      };
    }

    // For user messages, return as-is
    return {
      id: msg.id || `msg-${Date.now()}`,
      role: 'user',
      content: msg.content,
      timestamp: formatAIChatTime(new Date(msg.timestamp)),
      suggestedActions: [],
      processingTime: msg.processingTime,
      dcfId: null,
      widget: null
    };
  };

  // Check if widget is being repeated in recent messages
  const isWidgetRepeating = (widgetType, recentCount = 2) => {
    if (!widgetType) return false;

    const assistantMessages = messages.filter(m => m.role === 'assistant');
    const recentMessages = assistantMessages.slice(-recentCount);

    const sameWidgetCount = recentMessages.filter(m =>
      m.widget && m.widget.type === widgetType
    ).length;

    return sameWidgetCount >= recentCount;
  };

  // Parse agent response from GraphQL format
  const parseAgentResponse = (response) => {
    console.log('parseAgentResponse called with:', response);
    let widget = null;
    let message = response;
    let dcfId = null;

    // Extract widget
    const widgetMatch = response.match(/\[WIDGET:([\w_]+)\](.*?)\[\/WIDGET\]/s);
    console.log('Widget match:', widgetMatch);
    if (widgetMatch) {
      try {
        widget = {
          type: widgetMatch[1],
          data: JSON.parse(widgetMatch[2])
        };
        console.log('Parsed widget:', widget);
        message = response.replace(/\[WIDGET:[\w_]+\].*?\[\/WIDGET\]/s, '').trim();
      } catch (e) {
        console.error('Failed to parse widget data:', e);
      }
    }

    // Extract DCF ID
    const dcfMatch = response.match(/DCF_ID:(\S+)/);
    if (dcfMatch) {
      dcfId = dcfMatch[1];
      message = message.replace(/DCF_ID:\S+\.?/, '').trim();

      if (dcfId) {
        setCurrentDcfId(dcfId);
      }
    }

    // Extract suggested actions
    let suggestedActions = null;
    const actionsMatch = message.match(/Suggested actions: (.+)/);
    if (actionsMatch) {
      suggestedActions = actionsMatch[1].split(', ')
        .map(s => s.trim())
        .filter(s => s && !/^update$/i.test(s) && !/^complete$/i.test(s));

      if ((!suggestedActions || suggestedActions.length === 0) && dcfId) {
        suggestedActions = ['Open valuation report'];
      }
      message = message.replace(/\nSuggested actions: .+/, '').trim();
    }

    return {
      message: fixMarkdownLineBreaks(message),
      widget,
      suggestedActions,
      dcfId
    };
  };

  // Handle message sending
  const handleSendMessage = async (messageText) => {
    if (!messageText.trim() || isThinking) return;

    // Add user message immediately
    const userMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: messageText.trim(),
      timestamp: formatAIChatTime(new Date())
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setModalMessages(newMessages);
    setIsThinking(true);

    try {
      if (!agentId) {
        throw new Error('Agent not initialized');
      }

      const { data: interaction } = await interactWithAgent({
        variables: {
          agentType: 'valuation_chat_agent',
          message: messageText,
          conversationId: agentId, // Pass existing conversation ID to maintain history
          context: {
            chat_data: chatData,
            user_id: user.id,
            dcf_id: currentDcfId || dcfId || null,
            page_context: pageContext,
            client_state: clientState // Always sync current state
          }
        }
      });

      if (interaction && interaction.interactWithAgent && interaction.interactWithAgent.response) {
        const parsedResponse = parseAgentResponse(interaction.interactWithAgent.response);

        if (parsedResponse.updatedChatData) {
          setChatData(parsedResponse.updatedChatData);
        }

        // Check if DCF was updated on the valuation page
        if (interaction.interactWithAgent.updatedDcfParams && onDcfUpdate) {
          console.log('DCF was updated by agent:', interaction.interactWithAgent.updatedDcfParams);
          onDcfUpdate(interaction.interactWithAgent.updatedDcfParams);
        }

        // Also check for widgetType and widgetProps from GraphQL response
        let finalWidget = parsedResponse.widget;
        if (!finalWidget && interaction.interactWithAgent.widgetType && interaction.interactWithAgent.widgetProps) {
          finalWidget = {
            type: interaction.interactWithAgent.widgetType,
            data: interaction.interactWithAgent.widgetProps
          };
        }

        // Check if widget is being repeated - suppress if so
        if (finalWidget && isWidgetRepeating(finalWidget.type, 2)) {
          console.warn('Widget is repeating - suppressing to avoid redundancy:', finalWidget.type);
          finalWidget = null; // Suppress the widget

          // Add a note to the message if the response seems unhelpful
          if (parsedResponse.message.length < 200) {
            parsedResponse.message += "\n\n*(Note: I seem to be repeating myself. Could you ask your question in a different way, or let me know what specific aspect you'd like to focus on?)*";
          }
        }

        const agentMessage = {
          id: `msg-${Date.now() + 1}`,
          role: 'assistant',
          content: parsedResponse.message,
          timestamp: formatAIChatTime(new Date()),
          widget: finalWidget,
          suggestedActions: parsedResponse.suggestedActions,
          dcfId: parsedResponse.dcfId || null,
          ragSources: interaction.interactWithAgent.ragSources || []
        };

        const updatedMessages = [...newMessages, agentMessage];
        setMessages(updatedMessages);
        setModalMessages(updatedMessages);
      } else {
        throw new Error('No response from agent');
      }
    } catch (error) {
      console.error('Error processing message:', error);

      const errorMessage = {
        id: `msg-${Date.now() + 1}`,
        role: 'assistant',
        content: intl.formatMessage({ id: 'succession.agent_system.valuation_chat_window.error_rephrasing' }),
        timestamp: formatAIChatTime(new Date())
      };

      const updatedMessages = [...newMessages, errorMessage];
      setMessages(updatedMessages);
      setModalMessages(updatedMessages);
    } finally {
      setIsThinking(false);
    }
  };

  // Start a new conversation
  const startNewConversation = async () => {
    try {
      setMessages([]);
      setModalMessages([]);
      setAgentId(null);
      setChatData({});
      setConversationInfo(null);
      setCurrentDcfId(null);
      setIsLoadingHistory(true);

      const result = await interactWithAgent({
        variables: {
          agentType: 'valuation_chat_agent',
          message: 'START_NEW_CONVERSATION',
          context: {
            session_id: null,
            chat_data: {},
            reset: true
          }
        }
      });

      if (result.data?.interactWithAgent) {
        const response = result.data.interactWithAgent;
        setAgentId(response.agentId || `agent_${Date.now()}`);
        setConversationInfo(response.conversationInfo);

        if (response.response) {
          const welcomeMsg = createWelcomeMessage(response.response);
          setMessages([welcomeMsg]);
          setModalMessages([welcomeMsg]);
        }
      }
    } catch (error) {
      console.error('Failed to start new conversation:', error);
      const fallbackWelcome = createFallbackWelcome();
      setMessages([fallbackWelcome]);
      setModalMessages([fallbackWelcome]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Suggested topics for the empty state
  const suggestedTopics = [
    {
      id: 'confidence',
      title: "What's my valuation confidence score?",
      description: 'Understand your current valuation accuracy and completeness'
    },
    {
      id: 'improve',
      title: 'How can I improve my valuation?',
      description: 'Get personalized recommendations to increase confidence'
    },
    {
      id: 'historical',
      title: 'Help me add historical data',
      description: 'Learn why historical data matters and how to add it'
    },
    {
      id: 'missing',
      title: 'What data am I missing?',
      description: 'Identify gaps in your valuation profile'
    }
  ];

  return (
    <EnhancedAssistantModalV2
      open={true}
      onOpenChange={(open) => !open && onClose()}
      suggestedTopics={isLoadingHistory ? [] : suggestedTopics}
      onSendMessage={handleSendMessage}
      companyBrandedName="Valuation Assistant"
      messages={modalMessages}
      isThinking={isThinking}
      currentDcfId={currentDcfId}
      isLoadingHistory={isLoadingHistory}
    />
  );
};

/**
 * EnhancedAssistantModalV2 - Extended version with valuation-specific features
 */
const EnhancedAssistantModalV2 = ({
  open,
  onOpenChange,
  suggestedTopics,
  onSendMessage,
  companyBrandedName,
  messages: externalMessages,
  isThinking: externalThinking,
  currentDcfId,
  isLoadingHistory
}) => {
  const intl = useIntl();
  const [input, setInput] = useState('');
  const scrollRef = useRef(null);
  const forceScrollRef = useRef(false);
  const pinnedRef = useRef(true);

  const scrollToBottom = (smooth = true) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  };

  // Maintain pinned scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    if (atBottom || forceScrollRef.current || pinnedRef.current) {
      scrollToBottom(!atBottom);
      forceScrollRef.current = false;
    }
  }, [externalMessages, externalThinking]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
      pinnedRef.current = atBottom;
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const isEmpty = externalMessages.length === 0 && !externalThinking && !isLoadingHistory;

  const handleSend = () => {
    const text = input.trim();
    if (!text || externalThinking) return;

    onSendMessage(text);
    setInput('');
    forceScrollRef.current = true;
    pinnedRef.current = true;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === 'Escape') {
      onOpenChange?.(false);
    }
  };

  return (
    <Modal
      open={!!open}
      onClose={() => onOpenChange?.(false)}
      overlayClassName="fixed inset-0 z-50 bg-black/40"
      panelClassName="absolute inset-0 flex items-center justify-center p-2"
      className="bg-succession-bg-secondary text-succession-text-primary rounded-xl shadow-succession-lg border border-succession-border w-[92vw] h-[95vh] flex flex-col"
    >
      <div className="flex flex-col h-full w-full"  style={{ maxHeight: '95vh', maxWidth: '92vw' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0 bg-succession-bg-primary">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-succession-secondary/10 flex items-center justify-center">
              <SparklesIcon className="h-5 w-5 text-succession-secondary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold m-0 p-0" style={{ border: '0px solid black' }}>{companyBrandedName}</h2>
              <p className="text-sm text-succession-text-secondary m-0 p-0">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_window.ai_powered_insights' })}</p>
            </div>
          </div>
          <button
            onClick={() => onOpenChange?.(false)}
            className="flex-shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-succession-bg-primary text-succession-text-secondary"
            aria-label={intl.formatMessage({ id: 'succession.agent_system.valuation_chat_window.close' })}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-6" style={{ minHeight: 0, maxHeight: '100%' }}>
          {isLoadingHistory ? (
            <div className="flex justify-center items-center h-32">
              <div className="flex items-center space-x-2 text-succession-text-secondary">
                <SparklesIcon className="h-5 w-5 animate-pulse text-succession-secondary" />
                <span>{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_window.loading_history' })}</span>
              </div>
            </div>
          ) : isEmpty ? (
            <div className="max-w-3xl mx-auto text-center space-y-8 py-8">
              <div className="space-y-3">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-succession-secondary/10">
                  <SparklesIcon className="h-8 w-8 text-succession-secondary" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold m-0 p-0">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_window.greeting' })}</h3>
                <p className="text-succession-text-secondary text-sm md:text-base max-w-prose mx-auto">
                  {intl.formatMessage({ id: 'succession.agent_system.valuation_chat_window.greeting_body' })}
                </p>
              </div>

              {Array.isArray(suggestedTopics) && suggestedTopics.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl mx-auto">
                  {suggestedTopics.map((topic) => (
                    <button
                      key={topic.id || topic.title}
                      onClick={() => {
                        onSendMessage(topic.title);
                        forceScrollRef.current = true;
                      }}
                      className="p-4 text-left rounded-lg border border-succession-border bg-succession-bg-primary hover:bg-succession-bg-tertiary transition-all duration-200 hover:shadow-succession-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring"
                    >
                      <div className="flex items-start gap-3">
                        <div className="h-5 w-5 rounded bg-succession-secondary/10 flex items-center justify-center mt-0.5">
                          <span className="h-1.5 w-1.5 rounded-full bg-succession-secondary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-succession-text-primary m-0 p-0">{topic.title}</p>
                          {topic.description ? (
                            <p className="text-xs text-succession-text-secondary mt-1">{topic.description}</p>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6">
              {externalMessages.map((m) => (
                <MessageWithEnhancements key={m.id} message={m} />
              ))}

              {externalThinking && (
                <div className="flex gap-4 justify-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-secondary/10 flex items-center justify-center">
                    <SparklesIcon className="h-4 w-4 text-succession-secondary" />
                  </div>
                  <div className="rounded-lg p-4 bg-succession-bg-primary border border-succession-border">
                    <div className="flex items-center gap-2">
                      <Loader2Icon className="h-4 w-4 text-succession-secondary animate-spin" />
                      <p className="text-sm text-succession-text-secondary m-0 p-0">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_window.thinking' })}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="border-t border-succession-border bg-succession-bg-primary px-6 py-4 flex-shrink-0">
          <div className="max-w-3xl mx-auto space-y-3">
            {/* DCF Link */}
            {currentDcfId && (
              <div className="flex justify-center">
                <a
                  href={`/succession/valuation?dcf_id=${currentDcfId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-succession-secondary text-white text-sm font-medium rounded-md hover:bg-succession-secondary/90 border border-succession-secondary transition-colors duration-200 shadow-sm"
                >
                  <SparklesIcon className="h-5 w-5 mr-2" />
                  {intl.formatMessage({ id: 'succession.agent_system.valuation_chat_window.view_full_report' })}
                </a>
              </div>
            )}

            {/* Input */}
            <div className="relative">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={intl.formatMessage({ id: 'succession.agent_system.valuation_chat_window.ask_placeholder' })}
                rows={1}
                className="w-full min-h-[52px] max-h-[200px] pr-12 resize-none rounded-md bg-succession-bg-secondary border border-succession-border text-succession-text-primary placeholder-succession-text-muted px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || externalThinking}
                size="sm"
                className="absolute right-2 bottom-2 h-8 w-8 p-0 rounded-full"
              >
                <SendIcon className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-succession-text-secondary text-center">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_window.disclaimer' })}</p>
          </div>
        </div>
      </div>
    </Modal>
  );
};

/**
 * MessageWithEnhancements - Render message with valuation-specific features
 */
const MessageWithEnhancements = ({ message }) => {
  const intl = useIntl();
  return (
    <div className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
      {/* Avatar for assistant */}
      {message.role === 'assistant' && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-secondary/10 flex items-center justify-center">
          <SparklesIcon className="h-4 w-4 text-succession-secondary" />
        </div>
      )}

      {/* Message Content */}
      <div className={`rounded-lg p-4 max-w-[80%] break-words ${
        message.role === 'user'
          ? 'bg-succession-secondary text-white'
          : 'bg-succession-bg-primary border border-succession-border relative group'
      }`}>
        {message.role === 'user' ? (
          <p className="text-sm whitespace-pre-wrap m-0 break-words text-white">{message.content}</p>
        ) : (
          <AssistantMessageContent message={message} />
        )}

        {/* RAG Sources display - collapsible if too many */}
        {message.role !== 'user' && (
          <CollapsibleSources sources={message.ragSources} collapsedLimit={2} />
        )}

        {/* DCF Link */}
        {message.dcfId && (
          <div className="mt-3">
            <a
              href={`/succession/valuation?dcf_id=${message.dcfId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1.5 bg-succession-secondary text-white text-xs font-medium rounded hover:bg-succession-secondary/90 transition-colors"
            >
              <SparklesIcon className="h-4 w-4 mr-1.5" />
              {intl.formatMessage({ id: 'succession.agent_system.valuation_chat_window.view_report' })}
            </a>
          </div>
        )}

        {/* Suggested Actions */}
        {message.suggestedActions && message.suggestedActions.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {message.suggestedActions.map((action, index) => (
              <button
                key={index}
                className="text-xs px-2 py-1 rounded bg-succession-secondary/10 text-succession-secondary hover:bg-succession-secondary/20 transition-colors"
              >
                {action}
              </button>
            ))}
          </div>
        )}

        {/* Timestamp inside bubble */}
        <p className={`text-xs mt-2 m-0 p-0 ${message.role === 'user' ? 'text-white/70' : 'text-succession-text-secondary'}`}>
          {message.timestamp}
        </p>
      </div>

      {/* Avatar for user */}
      {message.role === 'user' && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-bg-secondary flex items-center justify-center">
          <span className="text-xs font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_window.you' })}</span>
        </div>
      )}
    </div>
  );
};

export default ValuationChatWindow;
